import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import { useSorting, SortableHeader } from "../../../Utility/Sorting";
import Pagination from "../../../Utility/Pagination/Pagination";
import { RiUpload2Line } from "react-icons/ri";
import {
  selectExportsPreviewData,
  selectExportsPreviewSummary,
  selectExportsPreviewLoading,
} from "../../../Redux/Slices/exportsSlice";
import { ContentLoader } from "../../../Utility/Loader";

const ExportPreview = ({
  tryQbExportGenerate,
  onExportFormat,
  exportSubmitting = false,
  /** When false, QB CSV/XLSX export control is hidden (`QB_BILLING_EXPORT`). */
  canExportQb = false,
}) => {
  const previewData = useSelector(selectExportsPreviewData) || [];
  const summary = useSelector(selectExportsPreviewSummary);
  const loading = useSelector(selectExportsPreviewLoading);

  /* ===================== SORTING ===================== */
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(previewData, {
    defaultSortKey: "qbClientName",
    defaultSortDirection: "asc",
  });

  /* ===================== PAGINATION ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;

  const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const totalHoursDisplay = useMemo(() => {
    if (summary != null) return summary.totalHours;
    return sortedData.reduce((sum, row) => sum + (Number(row.hours) || 0), 0);
  }, [summary, sortedData]);

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="h4">Export Preview</Typography>
        {canExportQb ? (
          <div className="relative" ref={exportDropdownRef}>
            <Button
              variant="neutral"
              size="vsm"
              title="Export"
              className="btn-reset square-btn"
              data-tooltipp="Export"
              disabled={exportSubmitting}
              onClick={() => tryQbExportGenerate(() => setExportDropdownOpen(!exportDropdownOpen))}
            >
              <RiUpload2Line className="text-lg text-text-secondary" />
            </Button>

            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <Button
                  type="button"
                  disabled={exportSubmitting}
                  onClick={() => {
                    tryQbExportGenerate(() => {
                      setExportDropdownOpen(false);
                      onExportFormat?.("csv");
                    });
                  }}
                  className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <Typography variant="text2" component="span">
                    Export CSV
                  </Typography>
                </Button>
                <Button
                  type="button"
                  disabled={exportSubmitting}
                  onClick={() => {
                    tryQbExportGenerate(() => {
                      setExportDropdownOpen(false);
                      onExportFormat?.("xlsx");
                    });
                  }}
                  className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <Typography variant="text2" component="span">
                    Export XLSX
                  </Typography>
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="mb-4 max-w-[100%]">
        <div className="bg-surface-primary rounded-md px-3 py-2 flex justify-between items-center">
          <Typography variant="h3" className="text-text-secondary text-lg m-0">
            Total hours
          </Typography>
          <Typography variant="span1" className="font-semibold text-lg">
            {Number(totalHoursDisplay).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </div>
      </div>

      <ContentLoader loading={loading} className="min-h-[120px]">
        <div className="relative flex flex-col bg-surface-primary shadow-sm">
          <div className="flex-1 p-2">
            <div className="overflow-x-auto">
              <table className="w-full mb-0 clients-table">
                <thead className="bg-surface-secondary">
                  <tr>
                    <SortableHeader
                      columnKey="qbClientName"
                      label="QB Client Name"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="project"
                      label="Project"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="epicName"
                      label="Epic Name"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="hours"
                      label="Billable Hours"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="monthEndDate"
                      label="Month End Date"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="clientNotes"
                      label="Client Notes"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="vendorNotes"
                      label="Vendor Notes"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="clientTags"
                      label="Client Tags"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="vendorTags"
                      label="Vendor Tags"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                  </tr>
                </thead>

                <tbody>
                  {sortedData.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-6">
                        <Typography variant="span1" className="text-text-secondary">
                          No export data available
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <Typography variant="span1">{row.qbClientName}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">{row.project}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">{row.epicName || "—"}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">
                            {Number(row.hours).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </td>
                        <td className="whitespace-nowrap">
                          <Typography variant="span1">{row.monthEndDate || "—"}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">{row.clientNotes || "—"}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">{row.vendorNotes || "—"}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">{row.clientTags || "—"}</Typography>
                        </td>
                        <td>
                          <Typography variant="span1">{row.vendorTags || "—"}</Typography>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="px-4 py-3 border-t bg-surface-secondary">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </ContentLoader>
    </div>
  );
};

export default ExportPreview;
